import { z } from 'zod';
import { listQuerySchema } from '../common/pagination';
import { moneySchema } from '../money';

/**
 * Kundenspezifischer Verkaufspreis für einen Artikel (ADR 0104).
 *
 * Zwei Formen, exklusiv zueinander:
 * - **Festpreis** — der ausgehandelte Nettopreis („Kunde A zahlt 450 €").
 *   Bleibt stehen, wenn sich der Basis-VK ändert.
 * - **Rabattsatz** — Prozent auf den Basis-VK („Kunde A bekommt 12 %").
 *   Zieht Basis-VK-Änderungen automatisch mit.
 *
 * Bewusst OHNE Gültigkeitszeitraum und ohne Staffelmenge: Ein Sonderpreis gilt,
 * bis ihn jemand ändert (Entscheidung 2026-09-08). Die Mengenstaffeln des
 * Artikels (`ArticlePriceTier`) sind davon unberührt.
 */
export const customerArticlePriceResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  customerId: z.string().uuid(),
  customerNumber: z.string().nullable(),
  customerName: z.string(),
  articleId: z.string().uuid(),
  articleNumber: z.string(),
  articleName: z.string(),
  /** Basis-VK des Artikels — damit die Register den Vergleich zeigen können. */
  baseSalesPrice: moneySchema.nullable(),
  /** Festpreis; null, wenn die Vereinbarung ein Rabattsatz ist. */
  price: moneySchema.nullable(),
  /** Rabattsatz in Prozent; null, wenn die Vereinbarung ein Festpreis ist. */
  discountPercent: z.number().nullable(),
  /**
   * Der Preis, der im Beleg landet — Festpreis, oder Basis-VK minus Rabatt.
   * Serverseitig gerechnet (§6: kein Geld im Browser multiplizieren).
   * null, wenn ein Rabattsatz auf einen Artikel ohne Basis-VK trifft.
   */
  effectivePrice: moneySchema.nullable(),
  note: z.string().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type CustomerArticlePriceResponse = z.infer<typeof customerArticlePriceResponseSchema>;

/**
 * Listen-Query. Genau eine Achse ist Pflicht: entweder alle Sonderpreise EINES
 * Kunden (Kunden-Register) oder alle Sonderpreise EINES Artikels
 * (Artikel-Register). Eine Liste über alles wäre weder nützlich noch bezahlbar.
 */
export const customerArticlePriceListQuerySchema = listQuerySchema
  .extend({
    customerId: z.string().uuid().optional(),
    articleId: z.string().uuid().optional(),
  })
  .refine((value) => Boolean(value.customerId) !== Boolean(value.articleId), {
    message: 'Genau eines von customerId oder articleId angeben.',
    path: ['customerId'],
  });
export type CustomerArticlePriceListQuery = z.infer<typeof customerArticlePriceListQuerySchema>;

const priceFields = {
  /** Festpreis in Mikro-Einheiten als String (§6) — exklusiv zu `discountPercent`. */
  price: moneySchema.nullish(),
  discountPercent: z.number().min(0).max(100).nullish(),
  note: z.string().trim().max(500).nullish(),
};

/** Genau eine der beiden Formen — dasselbe prüft der CHECK in der Migration. */
const isExactlyOneForm = (value: { price?: unknown; discountPercent?: unknown }): boolean =>
  (value.price != null) !== (value.discountPercent != null);
const FORM_ERROR = {
  message: 'Entweder einen Festpreis oder einen Rabattsatz angeben — nicht beides.',
  path: ['price'],
};

export const createCustomerArticlePriceSchema = z
  .object({
    customerId: z.string().uuid(),
    articleId: z.string().uuid(),
    ...priceFields,
  })
  .refine(isExactlyOneForm, FORM_ERROR);
export type CreateCustomerArticlePriceInput = z.infer<typeof createCustomerArticlePriceSchema>;

/** Ändern: Kunde und Artikel stehen fest, nur die Kondition wechselt. */
export const updateCustomerArticlePriceSchema = z.object(priceFields).refine(isExactlyOneForm, FORM_ERROR);
export type UpdateCustomerArticlePriceInput = z.infer<typeof updateCustomerArticlePriceSchema>;
